import { useCallback, useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, CheckCircle2, XCircle, X, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { FLOATING_LABEL_STYLES } from '@/components/ui/form-field'
import { lookupCompanyByTaxCode } from '@/modules/Company/services'
import {
    createSupplier,
    checkSupplierTaxCode,
} from '@/modules/Contract/modules/Partner/services'

interface QuickCreateSupplierProps {
    onCancel: () => void
    refetchSuppliers: () => Promise<unknown>
}

export interface QuickCreateSupplierRef {
    createAndGetId: () => Promise<number | null>
    isValid: () => boolean
    hasCodeError: () => boolean
}

/** Input with hover:border-primary */
const StyledInput = ({
    className,
    ...props
}: React.ComponentProps<typeof Input>) => (
    <Input
        className={cn('hover:border-primary focus:border-primary', className)}
        {...props}
    />
)

/** Floating label - z-20, white bg to match input background */
const FloatingInput = ({
    label,
    required,
    error,
    children,
}: {
    label: string
    required?: boolean
    error?: string
    children: React.ReactNode
}) => (
    <div className="relative">
        <span
            className={cn(
                'absolute -top-2.5 left-2 px-1 text-[11px] z-20 pointer-events-none bg-background',
                error
                    ? FLOATING_LABEL_STYLES.error
                    : FLOATING_LABEL_STYLES.default
            )}
        >
            {label}
            {required && <span className="text-destructive ml-0.5">*</span>}
        </span>
        {children}
        {error && <p className="text-xs text-destructive mt-1">{error}</p>}
    </div>
)

/** Quick create supplier inline form */
export const QuickCreateSupplier = forwardRef<
    QuickCreateSupplierRef,
    QuickCreateSupplierProps
>(({ onCancel, refetchSuppliers }, ref) => {
    const { t } = useTranslation('contract')

    const [name, setName] = useState('')
    const [taxCode, setTaxCode] = useState('')
    const [email, setEmail] = useState('')
    const [phone, setPhone] = useState('')
    const [address, setAddress] = useState('')
    const [isCheckingCode, setIsCheckingCode] = useState(false)
    const [codeExists, setCodeExists] = useState<boolean | null>(null)
    const [isLookingUp, setIsLookingUp] = useState(false)
    const [lookupFound, setLookupFound] = useState<boolean | null>(null)
    const [isCreating, setIsCreating] = useState(false)

    const handleLookupTaxCode = useCallback(async () => {
        if (!taxCode?.trim()) {
            setLookupFound(null)
            return
        }
        setIsLookingUp(true)
        setLookupFound(null)
        try {
            const result = await lookupCompanyByTaxCode(taxCode)
            if (result?.result?.name) {
                setName(result.result.name)
                if (result.result.address) setAddress(result.result.address)
                if (result.result.phone) setPhone(result.result.phone)
                setLookupFound(true)
            } else {
                setLookupFound(false)
            }
        } catch {
            setLookupFound(false)
        } finally {
            setIsLookingUp(false)
        }
    }, [taxCode])

    const handleCheckTaxCode = useCallback(async () => {
        if (!taxCode?.trim()) {
            setCodeExists(null)
            return
        }
        setIsCheckingCode(true)
        try {
            const result = await checkSupplierTaxCode({
                partner_tax_code: taxCode,
            })
            setCodeExists(result.exists)
        } catch {
            setCodeExists(null)
        } finally {
            setIsCheckingCode(false)
        }
    }, [taxCode])

    useImperativeHandle(
        ref,
        () => ({
            isValid: () =>
                !!(name.trim() && taxCode.trim() && codeExists !== true),
            hasCodeError: () => codeExists === true,
            createAndGetId: async () => {
                if (!name.trim() || !taxCode.trim() || codeExists === true)
                    return null
                setIsCreating(true)
                try {
                    const result = await createSupplier({
                        data: {
                            partner_name: name,
                            partner_code: '',
                            partner_tax_code: taxCode,
                            partner_status: 'active',
                            ...(email && { partner_email: email }),
                            ...(phone && { partner_phone: phone }),
                            ...(address && { partner_address: address }),
                        },
                        attachments: [],
                    })
                    await refetchSuppliers()
                    return result?.result?.partner_id || null
                } catch {
                    return null
                } finally {
                    setIsCreating(false)
                }
            },
        }),
        [name, taxCode, email, phone, address, codeExists, refetchSuppliers]
    )

    const renderLookupStatus = () => {
        if (isLookingUp)
            return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        if (lookupFound === true)
            return <CheckCircle2 className="w-4 h-4 text-green-500" />
        if (lookupFound === false)
            return <XCircle className="w-4 h-4 text-yellow-500" />
        return <Search className="w-4 h-4 text-gray-400" />
    }

    const renderCodeStatus = () => {
        if (isCheckingCode)
            return <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
        if (codeExists === true)
            return <XCircle className="w-4 h-4 text-red-500" />
        if (codeExists === false)
            return <CheckCircle2 className="w-4 h-4 text-green-500" />
        return null
    }

    return (
        <div className="border border-primary/30 rounded-lg p-4 bg-background">
            <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-primary">
                    {t('create-new-supplier')}
                </span>
                <button
                    type="button"
                    onClick={onCancel}
                    className="text-gray-400 hover:text-gray-600"
                >
                    <X className="w-4 h-4" />
                </button>
            </div>

            {/* Tax Code with lookup + uniqueness check */}
            <div className="mb-4">
                <FloatingInput
                    label={`${t('partner_tax_code')} (${t('lookup-company-info')})`}
                    required
                    error={
                        codeExists
                            ? t('partner-tax-code-already-exists')
                            : undefined
                    }
                >
                    <StyledInput
                        value={taxCode}
                        onChange={(e) => setTaxCode(e.target.value)}
                        onBlur={() => {
                            handleLookupTaxCode()
                            handleCheckTaxCode()
                        }}
                        placeholder={t('enter-tax-code')}
                        className={cn(
                            'pr-10',
                            codeExists && 'border-destructive'
                        )}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {isCheckingCode || isLookingUp
                            ? renderLookupStatus()
                            : codeExists !== null
                              ? renderCodeStatus()
                              : renderLookupStatus()}
                    </div>
                </FloatingInput>
                {lookupFound === false && taxCode && !codeExists && (
                    <p className="text-xs text-yellow-600 mt-1">
                        {t('company-not-found')}
                    </p>
                )}
                {lookupFound === true && !codeExists && (
                    <p className="text-xs text-green-600 mt-1">
                        {t('company-info-filled')}
                    </p>
                )}
            </div>

            {/* Name */}
            <div className="mb-4">
                <FloatingInput label={t('partner_name')} required>
                    <StyledInput
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('partner_name')}
                    />
                </FloatingInput>
            </div>

            {/* Email & Phone */}
            <div className="grid grid-cols-2 gap-3 mb-4">
                <FloatingInput label={t('partner_email')}>
                    <StyledInput
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t('partner_email')}
                    />
                </FloatingInput>
                <FloatingInput label={t('partner_phone')}>
                    <StyledInput
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder={t('partner_phone')}
                    />
                </FloatingInput>
            </div>

            {/* Address */}
            <FloatingInput label={t('partner_address')}>
                <StyledInput
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder={t('partner_address')}
                />
            </FloatingInput>

            {isCreating && (
                <div className="flex items-center gap-2 mt-3 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('creating-supplier')}...
                </div>
            )}
        </div>
    )
})

QuickCreateSupplier.displayName = 'QuickCreateSupplier'
